import { AIChatMessage } from '../types/Chat';
import axiosInstance from './axiosCreator';

interface RelatedArticles {
  articleId: string;
  articleTitle: string;
}

export const sendMessage = async (
  messages: AIChatMessage[]
): Promise<{
  stream: ReadableStream<Uint8Array>;
  relatedArticlesPromise: Promise<RelatedArticles[]>;
}> => {
  try {
    const baseURL = axiosInstance.defaults.baseURL;
    const token = localStorage.getItem('authToken');

    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await fetch(`${baseURL}/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ messages }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (!response.body) {
      throw new Error('No response body received');
    }

    // Create a promise that will resolve with the match IDs
    let relatedArticlesResolve: (ids: RelatedArticles[]) => void;
    const relatedArticlesPromise = new Promise<RelatedArticles[]>((resolve) => {
      relatedArticlesResolve = resolve;
    });

    // Transform the stream to parse SSE format
    const stream = new ReadableStream({
      async start(controller) {
        const reader = response?.body?.getReader();
        const decoder = new TextDecoder();

        if (!reader) {
          throw new Error('No reader found');
        }

        try {
          // eslint-disable-next-line no-constant-condition
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value);
            const lines = chunk.split('\n');

            let currentEvent = '';
            let currentData = '';

            for (const line of lines) {
              if (line.startsWith('event: ')) {
                currentEvent = line.slice(7);
              } else if (line.startsWith('data: ')) {
                currentData = line.slice(6);

                if (currentData) {
                  try {
                    const jsonData = JSON.parse(currentData);

                    if (currentEvent === 'content' && jsonData.content) {
                      controller.enqueue(
                        new TextEncoder().encode(jsonData.content)
                      );
                    } else if (currentEvent === 'matches' && jsonData.matches) {
                      relatedArticlesResolve(jsonData.matches);
                    }
                  } catch (e) {
                    console.error('Failed to parse JSON:', e);
                  }
                }
              }
            }
          }
        } finally {
          reader.releaseLock();
          controller.close();
        }
      },
    });

    return { stream, relatedArticlesPromise };
  } catch (error) {
    console.error('An unexpected error occurred:', error);
    throw error;
  }
};
